import { useEffect, useMemo, useRef, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { createTicketCapture, deleteTicketDraft, getTicketCapture, saveTicketDraft } from '../api/ticketCaptures';
import { createPresentation, getIngredients, getInventoryUnits } from '../api/inventory';
import { getExpenseSubcategories } from '../api/expenses';
import { useAuth } from '../auth/AuthProvider';
import { canProposeIngredient } from '../auth/permissions';
import { DateTimeField } from './DateTimeField';
import { Modal } from './Modal';
import { FooterReason } from './capture';
import { ActionBar } from './ui/ActionBar';
import { useConfirm } from './ui/ConfirmDialog';
import { ProgressiveSection } from './ui/ProgressiveSection';
import { InlineSupplierModal } from './PresentationEditor';
import { IngredientQuickCreateModal } from './IngredientQuickCreateModal';
import { applyTaxToAmount } from '../utils/purchaseTax';
import { selectableSupplierOptions } from '../utils/supplierOptions';
import { compatibleUnitOptions } from '../utils/units';
import { unitWordPlural } from '../utils/unitWording';
import { formatMoney, formatQuantity } from '../utils/costFormatting';
import { resolveStoreTimezone } from '../utils/timezones';
import { ticketReconciliation } from '../utils/ticketCapture';
import { EXPENSE_CATEGORY_LABELS } from '../utils/expenseActions';
import {
  dateBlocker, deriveInventoryLine, headerSteps, initialStep, newExpenseLine, newInventoryLine, nextHeaderStep, normalizeDraftPayload,
  paymentBlocker, previousStep, resolvePresentation, stepBlocker, supplierBlocker, ticketProgress, toDraftPayload, toTicketLine, toTicketLines,
  type WizardHeader, type WizardInventoryLine, type WizardLine, type WizardStep,
} from '../utils/ticketWizard';
import type { ExpenseCategory, ExpenseSubcategory } from '../types/expenses';
import type { Ingredient, InventoryUnit, PurchasePresentation, Supplier } from '../types/inventory';
import type { Store } from '../types/stores';
import type { TicketCaptureView, TicketPaymentMethod } from '../types/ticketCapture';

// Captura de ticket guiada (FI-20, docs/PHASE_GUIDED_TICKET_CAPTURE_DESIGN.md): «una pregunta a la
// vez», en el orden en que se lee el ticket físico. Reemplaza por completo al formulario anterior
// (decisión del dueño 1a). El contrato con el backend es el del Ticket Mixto C+: no cambia.
//
// Reglas de pantalla: ninguna lista llega preseleccionada (hay que elegir a propósito); el botón de
// avanzar dice qué falta; un bloque ya contestado se ENCOGE a una línea con «Cambiar» para no estorbar
// a lo que falta; al «Agregar al ticket» el renglón se minimiza y el foco pasa al siguiente (3a); los
// borradores del formulario anterior se abren aquí sin perder nada.

/** Decisión del dueño tras el preview (2026-09-05): 6 pantallas. Con `false` vuelve el detallado de 8. */
const COMPACT_STEPS = true;

const PAYMENT_OPTIONS: Array<[TicketPaymentMethod, string]> = [
  ['CASH', 'Efectivo'],
  ['CARD', 'Tarjeta'],
  ['TRANSFER', 'Transferencia'],
  ['CREDIT', 'A crédito (aún no pagado)'],
  ['OTHER', 'Otro'],
];

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const CATEGORY_OPTIONS = Object.entries(EXPENSE_CATEGORY_LABELS) as Array<[ExpenseCategory, string]>;
const MAX_INGREDIENT_MATCHES = 40;

const STEP_TITLES: Record<WizardStep, string> = {
  STORE: 'Tienda',
  SUPPLIER: 'Proveedor',
  WHEN: 'Cuándo y cómo',
  HEAD: 'Proveedor, fecha y pago',
  TOTAL: 'Total del ticket',
  KIND: 'Renglones',
  ITEM: 'Insumo',
  PACKAGING: 'Empaque',
  AMOUNT: 'Cantidad e importe',
  EXPENSE: 'Gasto',
  REVIEW: 'Revisar',
};

type HeaderBlock = 'supplier' | 'date' | 'payment';
const NOTHING_EXPANDED: Record<HeaderBlock, boolean> = { supplier: false, date: false, payment: false };
const onlyExpanded = (block: HeaderBlock): Record<HeaderBlock, boolean> => ({ ...NOTHING_EXPANDED, [block]: true });

/** Búsqueda sin acentos ni mayúsculas: «azucar» encuentra «Azúcar». */
function normalize(text: string) { return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase(); }
function formatWhen(iso: string, timeZone: string) {
  const text = new Date(iso).toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', timeZone });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function TicketCaptureModal({ stores, suppliers, defaultTaxRate, onClose, onSaved, onSupplierCreated, resumeDraft, onDraftsChanged, onOpenPurchase }: {
  stores: Store[];
  suppliers: Supplier[];
  defaultTaxRate: number | null;
  onClose: () => void;
  onSaved: () => void;
  onSupplierCreated: (supplier: Supplier) => void;
  /** P-078 (BK07): «Abrir la compra» desde el resumen del ticket guardado (abre su detalle en Compras). */
  onOpenPurchase?: (purchaseId: string) => void;
  /** Borrador que se está retomando; ausente cuando la captura empieza en blanco. */
  resumeDraft?: { id: string; payload: unknown } | null;
  onDraftsChanged?: () => void;
}) {
  const { user } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE';
  const confirm = useConfirm();
  const storeCount = stores.length;
  const [idempotencyKey] = useState(newId);

  // Estado inicial: el borrador (viejo o nuevo) normalizado. La tienda única se resuelve sola (no es
  // una lista que elegir). La fecha NO llega prellenada: se elige Hoy, Ayer u Otra fecha.
  const [restored] = useState(() => {
    const { header, lines } = normalizeDraftPayload(resumeDraft?.payload);
    const initialHeader: WizardHeader = { ...header, storeId: header.storeId || (storeCount === 1 ? stores[0]!.id : '') };
    const dateReady = Boolean(initialHeader.dateIso);
    return { header: initialHeader, lines, dateReady, step: initialStep(initialHeader, storeCount, lines.length, dateReady, COMPACT_STEPS) };
  });
  const [header, setHeader] = useState<WizardHeader>(restored.header);
  const [lines, setLines] = useState<WizardLine[]>(restored.lines);
  const [step, setStep] = useState<WizardStep>(restored.step);
  const [editing, setEditing] = useState<WizardLine | null>(null);
  const [dateReady, setDateReady] = useState(restored.dateReady);
  const [expanded, setExpanded] = useState<Record<HeaderBlock, boolean>>(NOTHING_EXPANDED);
  // P-079: «Cambiar insumo» reabre la lista con el insumo actual marcado; solo elegir OTRO borra el empaque y las
  // cantidades (Codex R1 · K11: antes «Cambiar» lo borraba todo antes de elegir nada).
  const [pickingIngredient, setPickingIngredient] = useState(false);
  // Al reabrir la lista desaparece el botón «Cambiar insumo»: el foco pasa a la lista (su contenedor, no el buscador,
  // para no levantar el teclado) si se quedó sin dónde estar (Codex R2 · R1).
  const ingredientListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickingIngredient) return;
    const active = document.activeElement;
    if (!active || active === document.body) ingredientListRef.current?.focus({ preventScroll: true });
  }, [pickingIngredient]);
  const [search, setSearch] = useState('');
  const [newPresentationOpen, setNewPresentationOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState<Record<string, ExpenseSubcategory[]>>({});
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [result, setResult] = useState<TicketCaptureView>();

  useEffect(() => {
    let alive = true;
    Promise.all([getIngredients(), getInventoryUnits()])
      .then(([ingredientsResult, unitsResult]) => { if (alive) { setIngredients(ingredientsResult.ingredients); setUnits(unitsResult.units); } })
      .catch(() => { /* la lista de insumos queda vacía; el error real saldrá al guardar */ });
    return () => { alive = false; };
  }, []);

  const ensureSubcategories = (category: ExpenseCategory) => {
    if (subcategoriesByCategory[category]) return;
    getExpenseSubcategories(category)
      .then((subs) => setSubcategoriesByCategory((current) => ({ ...current, [category]: subs.items })))
      .catch(() => setSubcategoriesByCategory((current) => ({ ...current, [category]: [] })));
  };

  const unitSymbolOf = (unitId: string) => units.find((unit) => unit.id === unitId)?.symbol ?? '';
  const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);
  const ingredientById = (id: string) => ingredients.find((ingredient) => ingredient.id === id);
  const patchHeader = (patch: Partial<WizardHeader>) => setHeader((current) => ({ ...current, ...patch }));
  const patchEditing = (patch: Partial<WizardInventoryLine> | Partial<WizardLine>) => setEditing((current) => (current ? { ...current, ...patch } as WizardLine : current));

  const parsedLines = useMemo(() => toTicketLines(lines, ingredientById, unitSymbolOf), [lines, ingredients, units]); // eslint-disable-line react-hooks/exhaustive-deps
  const physicalTotal = Number(header.totalText);
  const totalValid = Number.isFinite(physicalTotal) && physicalTotal > 0;
  const progress = ticketProgress(parsedLines, header.totalText);
  const reconciliation = ticketReconciliation(parsedLines, totalValid ? physicalTotal : 0, header.adjustmentNote);
  const editingIngredient = editing?.kind === 'INVENTORY' ? ingredientById(editing.ingredientId) : undefined;
  const editingPresentation = editing?.kind === 'INVENTORY' ? resolvePresentation(editing, editingIngredient, unitSymbolOf) : null;
  const derived = editing?.kind === 'INVENTORY' ? deriveInventoryLine(editing, editingPresentation, unitSymbolOf) : null;
  const stepContext = { storeCount, dateReady, editing, presentation: editingPresentation, linesCount: lines.length, reconciliationState: reconciliation.state, compact: COMPACT_STEPS };
  const blocker = stepBlocker(step, header, stepContext);
  const steps = headerSteps(storeCount, COMPACT_STEPS);
  const headerDone = steps.every((headerStep) => !stepBlocker(headerStep, header, { ...stepContext, editing: null, presentation: null }));
  const hasContent = lines.length > 0 || totalValid || Boolean(editing && (editing.kind === 'INVENTORY' ? editing.ingredientId : editing.description));
  const timeZone = resolveStoreTimezone(stores, { storeId: header.storeId || undefined });
  const selectedSupplier = header.supplierMode === 'SUPPLIER' ? suppliers.find((supplier) => supplier.name === header.supplierName) : undefined;
  const merchantLabel = header.supplierMode === 'SUPPLIER' ? header.supplierName : header.supplierMode === 'OTHER' ? header.merchantFree.trim() : 'Sin proveedor';

  // ── Navegación ──────────────────────────────────────────────────────────────────────────────
  const startLine = (kind: 'INVENTORY' | 'EXPENSE') => {
    setNotice(undefined);
    setEditing(kind === 'INVENTORY' ? newInventoryLine(newId()) : newExpenseLine(newId()));
    setSearch('');
    setPickingIngredient(false);
    setNewPresentationOpen(false);
    setStep(kind === 'INVENTORY' ? 'ITEM' : 'EXPENSE');
  };
  const editLine = (line: WizardLine) => {
    setNotice(undefined);
    setEditing({ ...line });
    setPickingIngredient(false);
    if (line.kind === 'EXPENSE') { setStep('EXPENSE'); return; }
    setNewPresentationOpen(Boolean(line.newPresentation));
    setStep(!line.ingredientId ? 'ITEM' : !line.mode ? (COMPACT_STEPS ? 'ITEM' : 'PACKAGING') : 'AMOUNT');
  };
  const removeLine = (id: string) => setLines((current) => current.filter((line) => line.id !== id));
  const goBack = () => {
    const previous = previousStep(step, storeCount, editing, COMPACT_STEPS);
    if (previous === 'KIND') setEditing(null);
    setStep(previous);
  };
  const chooseIngredient = (ingredient: Ingredient) => {
    setPickingIngredient(false);
    // Volver a elegir el MISMO insumo tras «Cambiar» no borra nada; otro insumo sí invalida su empaque y sus cantidades.
    if (editing?.kind === 'INVENTORY' && editing.ingredientId === ingredient.id) { if (!COMPACT_STEPS) setStep('PACKAGING'); return; }
    patchEditing({ ingredientId: ingredient.id, mode: '', presentationId: '', newPresentation: null, packageCount: '', unitId: '', quantity: '' });
    setNewPresentationOpen(false);
    // Compacto: el empaque aparece debajo, en la misma pantalla. Detallado: pantalla propia.
    if (!COMPACT_STEPS) setStep('PACKAGING');
  };
  const choosePresentation = (presentation: PurchasePresentation) => {
    patchEditing({ mode: 'PRESENTATION', presentationId: presentation.id, newPresentation: null, unitId: presentation.unit.id });
    setNewPresentationOpen(false);
    setStep('AMOUNT');
  };
  const chooseLoose = () => {
    patchEditing({ mode: 'LOOSE', presentationId: '', newPresentation: null, packageCount: '', unitId: '' });
    setNewPresentationOpen(false);
    setStep('AMOUNT');
  };
  const openNewPresentation = () => {
    if (editing?.kind !== 'INVENTORY') return;
    patchEditing({ mode: 'PRESENTATION', presentationId: '', newPresentation: editing.newPresentation ?? { name: '', quantity: '', unitId: '' } });
    setNewPresentationOpen(true);
  };
  const packagingStep: WizardStep = COMPACT_STEPS ? 'ITEM' : 'PACKAGING';

  /** «Agregar al ticket»: crea la presentación nueva si la hay, minimiza el renglón y vuelve a ¿Qué compraste? */
  const addToTicket = async () => {
    if (!editing || blocker || isSaving) return;
    let line: WizardLine = editing;
    let addedNotice: string | undefined;
    if (editing.kind === 'INVENTORY' && editing.mode === 'PRESENTATION' && !editing.presentationId && editing.newPresentation && derived?.quantity) {
      const draft = editing;
      const newPresentation = editing.newPresentation;
      const derivedQuantity = derived.quantity;
      const count = Number(draft.packageCount);
      const cost = Number((Number(draft.totalCost) / count).toFixed(4));
      setIsSaving(true);
      try {
        const { presentation } = await createPresentation(draft.ingredientId, { name: newPresentation.name.trim(), quantity: Number(newPresentation.quantity), unitId: newPresentation.unitId, cost, currency: 'MXN' });
        setIngredients((current) => current.map((ingredient) => (ingredient.id === draft.ingredientId ? { ...ingredient, presentations: [...ingredient.presentations, presentation] } : ingredient)));
        line = { ...draft, presentationId: presentation.id, newPresentation: null };
      } catch (createError) {
        // El ticket no se detiene por el catálogo: el renglón entra por cantidad suelta con lo que
        // ya se derivó (empaques × contenido) y se avisa; la presentación se puede crear después.
        line = { ...draft, mode: 'LOOSE', presentationId: '', newPresentation: null, packageCount: '', quantity: String(derivedQuantity), unitId: derived.unitId };
        addedNotice = `No se pudo guardar la presentación nueva (${getErrorMessage(createError, 'error desconocido')}). El renglón quedó por cantidad suelta: ${formatQuantity(String(derivedQuantity))} ${derived.unitSymbol}.`;
      } finally {
        setIsSaving(false);
      }
    }
    const finalLine = line;
    setLines((current) => (current.some((item) => item.id === finalLine.id) ? current.map((item) => (item.id === finalLine.id ? finalLine : item)) : [...current, finalLine]));
    setEditing(null);
    setNewPresentationOpen(false);
    setError(undefined);
    const row = describeLine(finalLine);
    setNotice(addedNotice ?? `Agregado al ticket: ${row.title} · ${row.amountLabel}`);
    setStep('KIND');
  };

  const goNext = () => {
    if (blocker) return;
    switch (step) {
      case 'STORE': case 'SUPPLIER': case 'WHEN': case 'HEAD': case 'TOTAL': setStep(nextHeaderStep(step, storeCount, COMPACT_STEPS)); break;
      case 'ITEM': setStep(COMPACT_STEPS ? 'AMOUNT' : 'PACKAGING'); break;
      case 'PACKAGING': setStep('AMOUNT'); break;
      case 'AMOUNT': case 'EXPENSE': void addToTicket(); break;
      case 'KIND': setStep('REVIEW'); break;
      case 'REVIEW': void complete(); break;
    }
  };

  const complete = async () => {
    if (blocker || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const { ticket } = await createTicketCapture({
        // Empleado con una sola tienda: el backend la resuelve; con varias, elige en P0 y se envía.
        storeId: isEmployee && storeCount <= 1 ? undefined : header.storeId || undefined,
        transactionDate: header.dateIso,
        paymentMethod: header.paymentMethod as TicketPaymentMethod,
        supplierId: selectedSupplier?.id ?? null,
        merchantDisplay: header.supplierMode === 'NONE' ? null : merchantLabel || null,
        physicalTicketTotal: physicalTotal,
        declaredAdjustmentNote: reconciliation.state === 'MISMATCH_DECLARED' ? header.adjustmentNote.trim() : null,
        idempotencyKey,
        lines: parsedLines,
      });
      setResult(ticket);
      // El ticket ya existe de verdad: el borrador pierde su razón de ser (si el borrado falla, no
      // se le dice nada a nadie — un borrador huérfano se descarta a mano).
      if (resumeDraft?.id) {
        await deleteTicketDraft(resumeDraft.id).catch(() => undefined);
        onDraftsChanged?.();
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el ticket.'));
    } finally {
      setIsSaving(false);
    }
  };

  /** Guardar sin terminar: solo deja anotado el asistente para volver. No crea compra ni gasto ni mueve caja. */
  const saveForLater = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const pending = editing && (editing.kind === 'INVENTORY' ? editing.ingredientId : editing.description.trim()) ? editing : null;
      const allLines = pending ? (lines.some((line) => line.id === pending.id) ? lines.map((line) => (line.id === pending.id ? pending : line)) : [...lines, pending]) : lines;
      await saveTicketDraft({
        id: resumeDraft?.id,
        storeId: header.storeId || null,
        label: merchantLabel === 'Sin proveedor' ? null : merchantLabel || null,
        lineCount: allLines.length,
        payload: toDraftPayload(header, allLines),
      });
      onDraftsChanged?.();
      onClose();
    } catch (draftError) {
      setError(getErrorMessage(draftError, 'No fue posible guardar el borrador.'));
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = async () => {
    if (isSaving) return;
    if (hasContent && !(await confirm({ title: '¿Descartar el ticket?', description: 'Lo capturado hasta ahora se perderá. Si prefieres, usa «Guardar para después».', confirmLabel: 'Descartar', cancelLabel: 'Seguir capturando', danger: true }))) return;
    onClose();
  };

  // ── Filas compactas («Ticket hasta ahora» y Revisar) ────────────────────────────────────────
  function describeLine(line: WizardLine): { title: string; secondary: string; amountLabel: string; complete: boolean } {
    const parsed = toTicketLine(line, line.kind === 'INVENTORY' ? ingredientById(line.ingredientId) : undefined, unitSymbolOf);
    const amount = Number(line.kind === 'INVENTORY' ? line.totalCost : line.amount);
    const amountLabel = Number.isFinite(amount) && amount > 0 ? formatMoney(String(amount)) : '—';
    if (line.kind === 'EXPENSE') {
      return { title: line.description.trim() || 'Gasto sin describir', secondary: line.category ? EXPENSE_CATEGORY_LABELS[line.category] : 'Falta la categoría', amountLabel, complete: parsed !== null };
    }
    const ingredient = ingredientById(line.ingredientId);
    const presentation = resolvePresentation(line, ingredient, unitSymbolOf);
    const lineDerived = deriveInventoryLine(line, presentation, unitSymbolOf);
    const parts: string[] = [];
    if (presentation && line.packageCount) parts.push(`${formatQuantity(line.packageCount)} × ${presentation.name}`);
    if (lineDerived.quantity) parts.push(`${formatQuantity(String(lineDerived.quantity))} ${lineDerived.unitSymbol}`);
    if (lineDerived.unitCostEcho) parts.push(lineDerived.unitCostEcho);
    return { title: ingredient?.name ?? 'Insumo sin elegir', secondary: parts.length ? parts.join(' · ') : 'Falta completar', amountLabel, complete: parsed !== null };
  }

  const linesList = (title: string) => lines.length > 0 && (
    <div className="wiz-lines">
      <h4>{title}</h4>
      {lines.map((line) => {
        const row = describeLine(line);
        return (
          <div className={`ticket-line ticket-line--compact${row.complete ? '' : ' ticket-line--incomplete'}`} key={line.id}>
            <button className="ticket-line__summary" onClick={() => editLine(line)} type="button">
              <span className={`ticket-line__tag${line.kind === 'EXPENSE' ? ' ticket-line__tag--expense' : ''}`}>{line.kind === 'INVENTORY' ? 'Insumo' : 'Gasto'}</span>
              <span className="ticket-line__what"><strong>{row.title}</strong><small>{row.complete ? row.secondary : `Falta completar · ${row.secondary}`}</small></span>
              <span className="ticket-line__amount">{row.amountLabel}</span>
              <span className="ticket-line__edit">Editar</span>
            </button>
            <button aria-label={`Quitar ${row.title}`} className="icon-button ticket-line__remove" onClick={() => removeLine(line.id)} type="button">×</button>
          </div>
        );
      })}
    </div>
  );

  if (result) {
    return <TicketResultModal approver={user?.role === 'OWNER' || user?.role === 'MANAGER'} ticket={result} onClose={() => { onSaved(); onClose(); }} onOpenPurchase={onOpenPurchase ? (purchaseId) => { onSaved(); onClose(); onOpenPurchase(purchaseId); } : undefined} />;
  }

  // ── Bloques del encabezado: contestado ⇒ una línea con «Cambiar»; abierto ⇒ la pregunta ───────
  // P-079: el patrón que nació aquí, con la pieza común (`ProgressiveSection`): completa = su blocker en null (la misma
  // validación de siempre); «Cambiar» reabre con el valor puesto; nada dice «guardado».
  const choice = (active: boolean, extra = '') => `wiz-choice${active ? ' wiz-choice--active' : ''}${extra ? ` ${extra}` : ''}`;
  const supplierBlock = (
    <ProgressiveSection busy={isSaving} complete={!supplierBlocker(header)} label="Proveedor" onOpen={() => setExpanded(onlyExpanded('supplier'))} open={expanded.supplier} summary={merchantLabel}>
      <h3 className="wiz-question">¿Quién fue el proveedor?</h3>
      <div className="wiz-answers">
        {/* Sin etiqueta «Proveedor del catálogo»: el propio desplegable lo dice. */}
        <div className="field wiz-field">
          <select aria-label="Proveedor del catálogo" onChange={(event) => { patchHeader({ supplierMode: 'SUPPLIER', supplierName: event.target.value, merchantFree: '' }); setExpanded(event.target.value ? NOTHING_EXPANDED : onlyExpanded('supplier')); }} value={header.supplierMode === 'SUPPLIER' ? header.supplierName : ''}>
            <option value="">Elige un proveedor del catálogo…</option>
            {selectableSupplierOptions(suppliers, header.supplierMode === 'SUPPLIER' ? header.supplierName : '').map(([name, label]) => <option key={name} value={name}>{label}</option>)}
          </select>
        </div>
        <div className="wiz-choices">
          <button className={choice(header.supplierMode === 'OTHER')} onClick={() => { patchHeader({ supplierMode: 'OTHER', supplierName: '' }); setExpanded(onlyExpanded('supplier')); }} type="button"><strong>Otro comercio</strong><small>Sin darlo de alta como proveedor</small></button>
          <button className={choice(header.supplierMode === 'NONE')} onClick={() => { patchHeader({ supplierMode: 'NONE', supplierName: '', merchantFree: '' }); setExpanded(NOTHING_EXPANDED); }} type="button"><strong>Sin proveedor</strong><small>No aplica o no lo sé</small></button>
        </div>
        {header.supplierMode === 'OTHER' && <label className="field"><span>Nombre del comercio</span><input autoFocus maxLength={200} onChange={(event) => patchHeader({ merchantFree: event.target.value })} placeholder="Ej. Súper del centro" value={header.merchantFree} /></label>}
      </div>
      <button className="link-button wiz-add" onClick={() => setSupplierModalOpen(true)} type="button">{isEmployee ? '+ Proponer proveedor nuevo' : '+ Agregar proveedor nuevo'}</button>
    </ProgressiveSection>
  );
  const dateBlock = (
    <ProgressiveSection busy={isSaving} complete={!dateBlocker(header, dateReady)} label="Fecha y hora" onOpen={() => setExpanded(onlyExpanded('date'))} open={expanded.date} summary={header.dateIso ? formatWhen(header.dateIso, timeZone) : ''}>
      <h3 className="wiz-question">¿Cuándo fue la compra?</h3>
      <div className="wiz-answers">
        <DateTimeField
          explicit
          label=""
          startAcknowledged={dateReady}
          value={header.dateIso || null}
          // Se encoge solo cuando la fecha/hora CAMBIA y queda lista. Al reabrir con «Cambiar», el campo
          // se monta ya completo y avisa «listo» con el mismo valor: eso no debe volver a encogerlo.
          onChange={(iso, ready) => { const changed = iso !== header.dateIso; patchHeader({ dateIso: iso }); setDateReady(ready); if (ready && changed) setExpanded((current) => ({ ...current, date: false })); }}
        />
      </div>
    </ProgressiveSection>
  );
  const paymentBlock = (
    <ProgressiveSection busy={isSaving} complete={!paymentBlocker(header)} label="Cómo pagaste" onOpen={() => setExpanded(onlyExpanded('payment'))} open={expanded.payment} summary={PAYMENT_OPTIONS.find(([value]) => value === header.paymentMethod)?.[1] ?? ''}>
      <h3 className="wiz-question">¿Cómo pagaste?</h3>
      <div className="wiz-choices wiz-answers">
        {PAYMENT_OPTIONS.map(([value, label]) => <button className={choice(header.paymentMethod === value)} key={value} onClick={() => { patchHeader({ paymentMethod: value }); setExpanded(NOTHING_EXPANDED); }} type="button"><strong>{label}</strong></button>)}
      </div>
    </ProgressiveSection>
  );

  // ── Empaque (pantalla propia en detallado; debajo del insumo en compacto) ───────────────────
  const renderPackaging = () => {
    const line = editing?.kind === 'INVENTORY' ? editing : null;
    const presentations = editingIngredient?.presentations.filter((presentation) => presentation.isActive) ?? [];
    const baseUnit = editingIngredient?.baseUnit;
    // «Cantidad suelta» siempre está (todo insumo puede comprarse sin empaque), con texto según cómo
    // se mide: por pieza, o a granel por peso o volumen. Va tras un separador cuando hay presentaciones.
    const looseLabel = !baseUnit
      ? <><strong>Cantidad suelta</strong><small>Sin empaque</small></>
      : baseUnit.type === 'COUNT'
        ? <><strong>Por pieza suelta</strong><small>Cuenta las piezas una por una ({baseUnit.symbol})</small></>
        : <><strong>A granel ({baseUnit.symbol})</strong><small>Sin empaque, por {baseUnit.type === 'MASS' ? 'peso' : baseUnit.type === 'VOLUME' ? 'volumen' : baseUnit.name.toLowerCase()}</small></>;
    return <>
      <h3 className="wiz-question">¿En qué venía {editingIngredient?.name ?? 'el insumo'}?</h3>
      <div className="wiz-choices wiz-choices--stack wiz-answers">
        {presentations.map((presentation) => (
          <button className={choice(line?.presentationId === presentation.id)} key={presentation.id} onClick={() => choosePresentation(presentation)} type="button">
            <strong>{presentation.name} · {formatQuantity(presentation.quantity)} {presentation.unit.symbol}</strong>
            <small>Contenido por {presentation.name.toLowerCase()}</small>
          </button>
        ))}
        {presentations.length > 0 && <div className="wiz-or">o si no venía empacado</div>}
        <button className={choice(line?.mode === 'LOOSE', 'wiz-choice--alt')} onClick={chooseLoose} type="button">{looseLabel}</button>
      </div>
      {/* «Agregar algo nuevo» es un enlace, no una respuesta. */}
      <button className="link-button wiz-add" onClick={openNewPresentation} type="button">+ Nueva presentación (un empaque que aún no está registrado)</button>
      {newPresentationOpen && line?.newPresentation && (
        <div className="form-grid form-grid--compact wiz-answers">
          {/* Orden pedido por el dueño: nombre → unidad → contenido (primero en qué se mide, luego cuánto). */}
          <label className="field"><span>Nombre del empaque</span><input autoFocus maxLength={120} onChange={(event) => patchEditing({ newPresentation: { ...line.newPresentation!, name: event.target.value } })} placeholder="Ej. Caja" value={line.newPresentation.name} /></label>
          <label className="field"><span>Unidad del contenido</span>
            <select onChange={(event) => patchEditing({ newPresentation: { ...line.newPresentation!, unitId: event.target.value } })} value={line.newPresentation.unitId}>
              <option value="">Elige la unidad…</option>
              {compatibleUnitOptions(units, editingIngredient?.baseUnit, line.newPresentation.unitId).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          {/* La etiqueta nombra la unidad elegida en el select de arriba: «Litros por empaque» quita la duda
              de si el contenido va en litros, bolsas o piezas. Mismo criterio que en las presentaciones. */}
          <label className="field"><span>{capitalizar(unitWordPlural(units.find((unit) => unit.id === line.newPresentation?.unitId)))} por empaque</span><input inputMode="decimal" min="0.0001" onChange={(event) => patchEditing({ newPresentation: { ...line.newPresentation!, quantity: event.target.value } })} placeholder="Ej. 100" step="any" type="number" value={line.newPresentation.quantity} /></label>
          <p className="section-hint form-grid__full">El costo del empaque se calcula solo con lo que pagues en el siguiente paso, y la presentación queda registrada para la próxima vez.</p>
        </div>
      )}
    </>;
  };

  // ── Cuerpo por paso ─────────────────────────────────────────────────────────────────────────
  const headerIndex = steps.indexOf(step);
  const filteredIngredients = (() => {
    const needle = normalize(search.trim());
    return ingredients.filter((ingredient) => ingredient.isActive && ingredient.approvalStatus !== 'REJECTED' && (!needle || normalize(ingredient.name).includes(needle)));
  })();

  let body: JSX.Element;
  switch (step) {
    case 'STORE':
      body = <>
        <h3 className="wiz-question">¿En qué tienda fue la compra?</h3>
        <div className="wiz-choices wiz-choices--stack wiz-answers">
          {stores.map((store) => <button className={choice(header.storeId === store.id)} key={store.id} onClick={() => patchHeader({ storeId: store.id })} type="button"><strong>{store.name}</strong></button>)}
        </div>
      </>;
      break;
    case 'SUPPLIER':
      body = supplierBlock;
      break;
    case 'WHEN':
      body = <>{dateBlock}{paymentBlock}</>;
      break;
    case 'HEAD':
      body = <>{supplierBlock}{dateBlock}{paymentBlock}</>;
      break;
    case 'TOTAL':
      body = <>
        <h3 className="wiz-question">¿Cuál fue el total del ticket?</h3>
        <p className="wiz-hint">Tal como lo dice el ticket, con todo incluido. Después iremos renglón por renglón hasta explicar este total.</p>
        {/* Enter / «Listo» del teclado: se cierra el teclado y, si el total es válido, avanza solo. */}
        <label className="field wiz-big-input"><span>Total pagado (MXN)</span><input autoFocus enterKeyHint="done" inputMode="decimal" min="0.01" onChange={(event) => patchHeader({ totalText: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); if (Number(event.currentTarget.value) > 0) setStep(nextHeaderStep('TOTAL', storeCount, COMPACT_STEPS)); } }} placeholder="0.00" step="any" type="number" value={header.totalText} /></label>
      </>;
      break;
    case 'KIND':
      body = <>
        {notice && <div className="alert alert--success" role="status">{notice}</div>}
        {linesList('Ticket hasta ahora')}
        <h3 className="wiz-question">{lines.length ? '¿Qué más traía el ticket?' : '¿Qué compraste?'}</h3>
        <div className="wiz-choices wiz-answers">
          <button className="wiz-choice" onClick={() => startLine('INVENTORY')} type="button"><strong>Insumo</strong><small>Entra a tu inventario</small></button>
          <button className="wiz-choice" onClick={() => startLine('EXPENSE')} type="button"><strong>Gasto del negocio</strong><small>Queda registrado en Gastos</small></button>
        </div>
        {lines.length > 0 && <p className="wiz-hint">¿Ya no hay más renglones? Usa <strong>Revisar</strong> para ver todo junto y completar.</p>}
      </>;
      break;
    case 'ITEM':
      if (COMPACT_STEPS && editing?.kind === 'INVENTORY' && editing.ingredientId && !pickingIngredient) {
        body = <>
          <ProgressiveSection busy={isSaving} complete label="Insumo" onOpen={() => setPickingIngredient(true)} summary={`${editingIngredient?.name ?? 'Insumo'}${editingIngredient ? ` · se mide en ${editingIngredient.baseUnit.symbol}` : ''}`} />
          {renderPackaging()}
        </>;
        break;
      }
      body = <>
        <h3 className="wiz-question">¿Cuál insumo?</h3>
        <div aria-label="¿Cuál insumo?" className="wiz-answers" ref={ingredientListRef} role="group" tabIndex={-1}>
          {/* Sin etiqueta ni autofoco: muchas veces se elige de la lista sin escribir, y el teclado taparía la lista. */}
          <div className="field wiz-field"><input aria-label="Buscar insumo" enterKeyHint="search" onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} placeholder="Buscar por nombre…" value={search} /></div>
          <div className="wiz-list">
            {filteredIngredients.slice(0, MAX_INGREDIENT_MATCHES).map((ingredient) => (
              <button className={choice(editing?.kind === 'INVENTORY' && editing.ingredientId === ingredient.id)} key={ingredient.id} onClick={() => chooseIngredient(ingredient)} type="button">
                <strong>{ingredient.name}</strong>
                <small>Se mide en {ingredient.baseUnit.name} ({ingredient.baseUnit.symbol}){ingredient.approvalStatus === 'PENDING_APPROVAL' ? ' · pendiente de aprobación' : ''}</small>
              </button>
            ))}
          </div>
          {filteredIngredients.length > MAX_INGREDIENT_MATCHES && <p className="wiz-hint">Hay más insumos; afina la búsqueda.</p>}
          {filteredIngredients.length === 0 && <p className="wiz-hint">{ingredients.length === 0 ? 'Cargando insumos…' : 'No hay insumos con ese nombre.'}</p>}
        </div>
        {canProposeIngredient(user?.role) && <button className="link-button wiz-add" onClick={() => setIngredientModalOpen(true)} type="button">{isEmployee ? '+ Proponer un insumo que no está en la lista' : '+ Agregar un insumo que no está en la lista'}</button>}
      </>;
      break;
    case 'PACKAGING':
      body = renderPackaging();
      break;
    case 'AMOUNT': {
      const line = editing?.kind === 'INVENTORY' ? editing : null;
      body = <>
        {line?.mode === 'PRESENTATION' ? (
          editingPresentation ? (
            <>
              <h3 className="wiz-question">¿Cuántas «{editingPresentation.name}» compraste?</h3>
              <label className="field wiz-big-input">
                <span>{editingPresentation.name} · {formatQuantity(String(editingPresentation.quantity))} {editingPresentation.unitSymbol} cada una</span>
                {/* «2» y, a su derecha, «= 8 L» grande: lo que pidió el dueño al ver el preview. */}
                <div className="wiz-inline">
                  <input autoFocus enterKeyHint="next" inputMode="decimal" min="0.01" onChange={(event) => patchEditing({ packageCount: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); document.getElementById('wiz-line-amount')?.focus(); } }} placeholder="0" step="any" type="number" value={line.packageCount} />
                  <div aria-live="polite" className={`wiz-echo wiz-echo--big${derived?.quantityEcho ? '' : ' is-empty'}`}>{derived?.quantityEcho ?? `= 0 ${editingPresentation.unitSymbol}`}</div>
                </div>
              </label>
            </>
          ) : (
            // Borrador con una presentación que ya no existe (o incompleta): no se adivina nada.
            <div className="alert alert--warning">La presentación de este renglón ya no está disponible. <button className="link-button" onClick={() => setStep(packagingStep)} type="button">Elegir en qué venía</button></div>
          )
        ) : (
          <>
            <h3 className="wiz-question">¿Cuánto compraste?</h3>
            {/* Orden pedido por el dueño: primero la unidad, luego la cantidad. Sin autofoco: el
                teclado no debe abrirse antes de elegir la unidad. */}
            <div className="form-grid form-grid--compact">
              <label className="field"><span>Unidad</span>
                <select onChange={(event) => patchEditing({ unitId: event.target.value })} value={line?.unitId ?? ''}>
                  <option value="">Elige la unidad…</option>
                  {compatibleUnitOptions(units, editingIngredient?.baseUnit, line?.unitId).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </label>
              <label className="field"><span>Cantidad{line?.unitId ? ` (${unitSymbolOf(line.unitId)})` : ''}</span><input enterKeyHint="next" inputMode="decimal" min="0.0001" onChange={(event) => patchEditing({ quantity: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); document.getElementById('wiz-line-amount')?.focus(); } }} placeholder="0" step="any" type="number" value={line?.quantity ?? ''} /></label>
            </div>
          </>
        )}
        <h3 className="wiz-question">¿Cuánto pagaste por este renglón?</h3>
        <label className="field"><span>Importe del renglón (MXN)</span>
          <div className="ticket-cost-row">
            <input enterKeyHint="done" id="wiz-line-amount" inputMode="decimal" min="0.01" onChange={(event) => patchEditing({ totalCost: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} placeholder="0.00" step="any" type="number" value={line?.totalCost ?? ''} />
            {defaultTaxRate !== null && <button className="button button--secondary ticket-iva" disabled={!Number(line?.totalCost)} onClick={() => line && patchEditing({ totalCost: applyTaxToAmount(Number(line.totalCost), defaultTaxRate) })} title={`Sumar ${defaultTaxRate}% de IVA al importe`} type="button">+IVA</button>}
          </div>
          <small>Si el ticket trae el precio sin impuesto, usa +IVA.</small>
        </label>
        {derived && (derived.perPackageEcho || derived.unitCostEcho) && <p className="wiz-echo">= {[derived.perPackageEcho, derived.unitCostEcho].filter(Boolean).join(' · ')}</p>}
        <button className="link-button" onClick={() => setStep(packagingStep)} type="button">Cambiar empaque o insumo</button>
      </>;
      break;
    }
    case 'EXPENSE': {
      const line = editing?.kind === 'EXPENSE' ? editing : null;
      body = <>
        <h3 className="wiz-question">¿Qué gasto fue?</h3>
        <div className="form-grid form-grid--compact">
          <label className="field"><span>¿Qué es?</span><input autoFocus maxLength={300} onChange={(event) => patchEditing({ description: event.target.value })} placeholder="Ej. Escoba" value={line?.description ?? ''} /></label>
          <label className="field"><span>Categoría</span>
            <select onChange={(event) => { const category = event.target.value as ExpenseCategory | ''; patchEditing({ category, subcategory: '' }); if (category) ensureSubcategories(category); }} value={line?.category ?? ''}>
              <option value="">Elige la categoría…</option>
              {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="field"><span>Subcategoría</span>
            <select disabled={!line?.category} onChange={(event) => patchEditing({ subcategory: event.target.value })} value={line?.subcategory ?? ''}>
              <option value="">Sin subcategoría</option>
              {(line?.category ? subcategoriesByCategory[line.category] ?? [] : []).map((sub) => <option key={sub.id} value={sub.name}>{sub.name}</option>)}
            </select>
          </label>
          <label className="field"><span>Importe (MXN)</span><input min="0.01" onChange={(event) => patchEditing({ amount: event.target.value })} placeholder="0.00" step="any" type="number" value={line?.amount ?? ''} /></label>
        </div>
      </>;
      break;
    }
    case 'REVIEW':
    default:
      body = <>
        <h3 className="wiz-question">Revisa el ticket antes de completar</h3>
        <div className="wiz-summary">
          <strong>{merchantLabel} · {totalValid ? formatMoney(String(physicalTotal)) : '—'}</strong>
          <span>{stores.find((store) => store.id === header.storeId)?.name ?? 'Tienda'} · {header.dateIso ? formatWhen(header.dateIso, timeZone) : '—'} · {PAYMENT_OPTIONS.find(([value]) => value === header.paymentMethod)?.[1] ?? 'Sin método'}</span>
          <button className="link-button" onClick={() => setStep(steps[0]!)} type="button">Cambiar datos del ticket</button>
        </div>
        {linesList('Renglones')}
        {lines.length === 0 && <p className="ticket-lines__empty">Todavía no hay renglones. Regresa y agrega lo que compraste.</p>}
        <div className={`ticket-verifier ticket-verifier--${reconciliation.state === 'MATCH' ? 'ok' : reconciliation.state === 'MISMATCH_DECLARED' ? 'warn' : 'pending'}`}>{reconciliation.message}</div>
        {reconciliation.state === 'MISMATCH_BLOCKED' && !adjustmentOpen && !header.adjustmentNote && (
          <button className="link-button" onClick={() => setAdjustmentOpen(true)} type="button">El ticket trae un ajuste o redondeo</button>
        )}
        {(adjustmentOpen || header.adjustmentNote) && (
          <label className="field"><span>Explica el ajuste/redondeo del ticket</span><input maxLength={500} onChange={(event) => patchHeader({ adjustmentNote: event.target.value })} placeholder="Ej. Redondeo del súper: $0.50" value={header.adjustmentNote} /></label>
        )}
      </>;
      break;
  }

  const primaryLabel = step === 'REVIEW' ? (isSaving ? 'Guardando...' : 'Completar') : step === 'AMOUNT' || step === 'EXPENSE' ? (isSaving ? 'Agregando...' : 'Agregar al ticket') : step === 'KIND' ? 'Revisar' : 'Siguiente';
  const primaryDisabled = isSaving || (step === 'KIND' ? lines.length === 0 : Boolean(blocker));
  const canGoBack = step !== steps[0];
  const footerReason = step === 'REVIEW' ? null : step === 'KIND' ? (lines.length === 0 ? 'Agrega el primer renglón para poder revisar.' : progress.label) : blocker;

  return <>
    <Modal
      size="wide"
      title="Capturar ticket"
      description={headerDone ? STEP_TITLES[step] : `Datos del ticket · paso ${Math.max(1, headerIndex + 1)} de ${steps.length} · ${STEP_TITLES[step]}`}
      isBusy={isSaving}
      onClose={() => void cancel()}
      // Pie callado: el motivo va en gris sin fondo (con fondo solo cuando es el avance del ticket); Cancelar y Guardar
      // para después son texto; el único botón sólido es el principal.
      // P-078 (B · H): el motivo ya no ocupa una celda junto a Cancelar: va en su renglón (nota de ActionBar v2) y la
      // primaria a todo el ancho en el teléfono. Mismas condiciones.
      footer={<ActionBar aria-label="Acciones del ticket" note={footerReason ? (step === 'KIND' && lines.length > 0
        ? <div className={`ticket-verifier ticket-verifier--${progress.over ? 'warn' : progress.remainingCents === 0 ? 'ok' : 'pending'}`}>{footerReason}</div>
        : <FooterReason>{footerReason}</FooterReason>) : undefined} primary={<button className="button button--primary" disabled={primaryDisabled} onClick={goNext} type="button">{primaryLabel}</button>}>
        <button className="text-button" disabled={isSaving} onClick={() => void cancel()} type="button">Cancelar</button>
        <button className="text-button" disabled={isSaving || !hasContent} onClick={() => void saveForLater()} type="button">Guardar para después</button>
        {canGoBack && <button className="button button--ghost" disabled={isSaving} onClick={goBack} type="button">Atrás</button>}
      </ActionBar>}
    >
      {error && <div className="alert alert--error">{error}</div>}
      {headerDone && (
        <div className="wiz-progress" aria-live="polite">
          <div className="wiz-progress__text"><span>Ticket hasta ahora</span><span>{progress.label}</span></div>
          <div className="wiz-progress__bar"><div className={`wiz-progress__fill${progress.over ? ' wiz-progress__fill--over' : ''}`} style={{ width: `${progress.percent}%` }} /></div>
        </div>
      )}
      {body}
    </Modal>

    {supplierModalOpen && (
      <InlineSupplierModal
        proposeOnly={isEmployee}
        onClose={() => setSupplierModalOpen(false)}
        onCreated={(supplier) => { onSupplierCreated(supplier); patchHeader({ supplierMode: 'SUPPLIER', supplierName: supplier.name, merchantFree: '' }); setExpanded(NOTHING_EXPANDED); setSupplierModalOpen(false); }}
      />
    )}
    {ingredientModalOpen && (
      <IngredientQuickCreateModal
        isProposal={isEmployee}
        units={units}
        onClose={() => setIngredientModalOpen(false)}
        onCreated={(ingredient) => {
          setIngredients((current) => [...current, ingredient]);
          setIngredientModalOpen(false);
          chooseIngredient(ingredient);
        }}
      />
    )}
  </>;
}

/** Resumen después de guardar: total, qué entrará a inventario, qué quedó en gastos y su estado. */
function TicketResultModal({ ticket, approver, onClose, onOpenPurchase }: { ticket: TicketCaptureView; /** Dueño o gerente: aprueba catálogo y compras, así que no se le habla de «tu dueño o gerente». */ approver: boolean; onClose: () => void; onOpenPurchase?: (purchaseId: string) => void }) {
  const pendingApproval = ticket.purchase?.status === 'PENDING_APPROVAL';
  // P-078 (BK07): el texto distingue quién lee (el aprobador lo ve así cuando el freno es catálogo pendiente) y, si hay
  // algo que hacer con la compra (aprobarla, reintentar la entrada), se ofrece abrirla aquí mismo.
  const openPurchase = onOpenPurchase && ticket.purchase && (pendingApproval || !ticket.purchase.stockPostedAt)
    ? <button className="link-button" onClick={() => onOpenPurchase(ticket.purchase!.id)} type="button">Abrir la compra</button>
    : null;
  return (
    <Modal
      title="Ticket guardado ✓"
      description={`${ticket.storeName} · Total $${ticket.physicalTicketTotal}`}
      onClose={onClose}
      footer={<button className="button button--primary" onClick={onClose} type="button">Listo</button>}
    >
      <div className="ticket-result">
        {ticket.purchase && (
          <div className="ticket-result__block ticket-result__block--doc">
            <span className="ticket-result__eyebrow">Compra</span>
            <strong>${ticket.inventoryTotal} en insumos</strong>
            {pendingApproval
              ? <span>{approver
                ? 'Quedó pendiente de aprobación (si un proveedor o insumo del ticket está pendiente de catálogo, se aprueba primero en Aprobaciones); el inventario entrará cuando se apruebe. El dinero del ticket ya cuenta desde hoy.'
                : 'Quedó en espera de aprobación de tu dueño o gerente; el inventario entrará cuando la aprueben. El dinero del ticket ya cuenta desde hoy.'} {openPurchase}</span>
              : ticket.purchase.stockPostedAt
                ? <span>Ya entró a tu inventario ({ticket.purchase.linesCount} insumo(s)).</span>
                : <span>Registrada; la entrada al inventario se puede reintentar desde Compras. {openPurchase}</span>}
            <small>{ticket.purchase.folio ?? 'Compra'}{ticket.purchase.supplierName ? ` · ${ticket.purchase.supplierName}` : ''}</small>
          </div>
        )}
        {ticket.expenses.length > 0 && (
          <div className="ticket-result__block ticket-result__block--doc">
            <span className="ticket-result__eyebrow">Gastos</span>
            <strong>${ticket.expensesTotal} en gastos del negocio</strong>
            <span>Quedaron registrados en Gastos.</span>
            {ticket.expenses.map((expense) => <small key={expense.id}>{expense.folio ?? 'Gasto'} · {expense.concept}: ${expense.amount}</small>)}
          </div>
        )}
        {ticket.declaredAdjustmentNote && <div className="ticket-result__block ticket-result__block--doc"><span className="ticket-result__eyebrow">Nota</span><small>Ajuste declarado: {ticket.declaredAdjustmentNote}</small></div>}
      </div>
    </Modal>
  );
}

/** Consulta documental del ticket desde un documento hijo ("Parte de un ticket que también generó…"). */
export function TicketInfoModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const [ticket, setTicket] = useState<TicketCaptureView>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let alive = true;
    getTicketCapture(ticketId)
      .then((view) => { if (alive) setTicket(view); })
      .catch((loadError) => { if (alive) setError(getErrorMessage(loadError, 'No fue posible cargar el ticket.')); });
    return () => { alive = false; };
  }, [ticketId]);
  return (
    <Modal title="Ticket de compra" description="Este registro nació de una captura única de ticket." onClose={onClose} footer={<button className="button button--primary" onClick={onClose} type="button">Cerrar</button>}>
      {error && <div className="alert alert--error">{error}</div>}
      {!ticket && !error && <div className="inline-loading"><span className="spinner" />Cargando…</div>}
      {ticket && (
        <div className="ticket-result">
          {/* El ticket fisico es el ORIGEN; lo de abajo son los documentos que genero. Antes los
              cuatro recuadros eran identicos y no se leia cual era la causa y cuales la consecuencia. */}
          <div className="ticket-result__block ticket-result__block--origin">
            <span className="ticket-result__eyebrow">Ticket físico</span>
            <strong>{ticket.merchantDisplay || ticket.storeName} · ${ticket.physicalTicketTotal}</strong>
            <span>{new Date(ticket.transactionDate).toLocaleString('es-MX')} · {PAYMENT_OPTIONS.find(([value]) => value === ticket.paymentMethod)?.[1] ?? ticket.paymentMethod}</span>
          </div>
          {ticket.purchase && <div className="ticket-result__block ticket-result__block--doc"><span className="ticket-result__eyebrow">Compra</span><strong>{ticket.purchase.folio ?? 'Compra'} · ${ticket.purchase.totalAmount} en insumos</strong><span>{ticket.purchase.status === 'PENDING_APPROVAL' ? 'En espera de aprobación' : ticket.purchase.stockPostedAt ? 'Inventario registrado' : 'Confirmada'}</span></div>}
          {ticket.expenses.map((expense) => <div className="ticket-result__block ticket-result__block--doc" key={expense.id}><span className="ticket-result__eyebrow">Gasto</span><strong>{expense.folio ?? 'Gasto'} · ${expense.amount}</strong><span>{expense.concept}</span></div>)}
          {ticket.declaredAdjustmentNote && <div className="ticket-result__block ticket-result__block--doc"><span className="ticket-result__eyebrow">Nota</span><small>Ajuste declarado: {ticket.declaredAdjustmentNote}</small></div>}
        </div>
      )}
    </Modal>
  );
}
